import React, { memo, useCallback, useEffect } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { ThreeDots } from 'react-loader-spinner';

import { Input } from '../../../../components/common-inputs';
import CommonButton from '../../../../components/common-button/CommonButton';
import Icon from '../../../../icons/Icon';
import { ICONS } from '../../../../icons';
import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import WithError from '../../../../components/with-error/WithError';
import passwordGenerator from '../../../../utils/passwordGeneration';
import PasswordInput from '../../../../components/common-inputs/PasswordInput';
import AssignProjects from './AssignProjects';

import scss from './AddUserForm.module.scss';

const AddUserForm = ({ onSubmit, assignedCompanyProjects }) => {
  const { t } = useTranslation('customers');
  const { restart, change } = useForm();
  const { submitting } = useFormState();

  const handleClear = useCallback(() => {
    restart();
  }, [restart]);

  const handleGeneratePassword = useCallback(() => {
    change('password', passwordGenerator({ length: 20 }));
  }, [change]);

  return (
    <div className={scss.form}>
      <div className={scss.fieldsWrapper}>
        <Field name='firstName'>
          {({ input, meta }) => (
            <WithLabel label={t('userName')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.commonField}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input size='sm' formProps={input} isError={meta.touched && meta.error} disabled={submitting} />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='lastName'>
          {({ input, meta }) => (
            <WithLabel label={t('surname')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.commonField}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input size='sm' formProps={input} isError={meta.touched && meta.error} disabled={submitting} />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='username'>
          {({ input, meta }) => (
            <WithLabel label={t('email')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.commonField}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input size='sm' formProps={input} isError={meta.touched && meta.error} disabled={submitting} />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='password'>
          {({ input, meta }) => (
            <WithLabel label={t('password')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.passwordField}>
              <WithError
                isShowError={meta.touched && meta.error}
                errorText={t(meta.error)}
                extraClassName={scss.errorWrapper}>
                <PasswordInput size='sm' formProps={input} disabled={submitting} isError={meta.touched && meta.error} />
              </WithError>

              <CommonButton
                styleColor='secondary'
                maxWidth={190}
                onClick={handleGeneratePassword}
                disabled={submitting}
                className={scss.generatePasswordButton}>
                <Icon icon={ICONS.Lock} />
                {t('generatePassword')}
              </CommonButton>
            </WithLabel>
          )}
        </Field>

        <AssignProjects assignedCompanyProjects={assignedCompanyProjects} />
      </div>

      <div className={scss.actions}>
        <CommonButton type='reset' styleColor='secondary' maxWidth={120} onClick={handleClear} disabled={submitting}>
          <Icon icon={ICONS.Delete} /> {t('delete')}
        </CommonButton>
        <CommonButton type='submit' onClick={onSubmit} maxWidth={120} disabled={submitting}>
          {submitting ? <ThreeDots height={15} width={40} /> : null}
          {t('addUser')}
        </CommonButton>
      </div>
    </div>
  );
};

export default memo(AddUserForm);
